/** @jsxImportSource @emotion/react */
import { Box } from '@mui/material'
import { useMemo, useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { DataLayerDetailError } from '../components/DataLayerDetailError'
import DataLayerDetailHeader, {
  DataLayerDetailHeaderBackButton,
  DataLayerDetailHeaderTitle,
} from '../components/DataLayerDetailHeader'
import DataLayerDetailMap from '../components/DataLayerDetailMap'
import { DataLayerDetailPageSkeleton } from '../components/DataLayerDetailPageSkeleton'
import DataLayerDetailTabs from '../components/DataLayerDetailTabs'
import { useDataLayerScenario } from '../hooks/useDataLayerScenario'
import { useLayerMetadata } from '../hooks/useLayerMetadata'
import { useResolvedLayer } from '../hooks/useResolvedLayer'
import { useSelectedLayerDetail } from '../hooks/useSelectedLayerDetail'
import { content, mapPane, page, sideInner } from '../styles/DataLayerDetailPage.styles'
import { container } from '../styles/DataLayout.styles'
import { AxiosError } from 'axios'

const DataLayerDetailPage = () => {
  const { layerId: id } = useParams<{ layerId: string }>()

  const layer = useResolvedLayer(id)
  const fetchedLayerMeta = useLayerMetadata(id)
  const { selectedLayerDetail, isLoading, isError, error } = useSelectedLayerDetail(id)

  const [selectedAssetTag, setSelectedAssetTag] = useState<string | null>(id ?? null)

  const { activeDisplayName, activeTilesetIds } = useDataLayerScenario({
    layerId: id || '',
    selectedAssetTag,
    onSelectScenario: setSelectedAssetTag,
    fallbackDisplayName: selectedLayerDetail?.displayName,
    fallbackTilesetIds: selectedLayerDetail?.tilesetIds,
  })

  const isMissingLayerDetail =
    isError && (error as AxiosError).response?.status == 404 && !!id && !!layer?.displayName

  const layerMeta = useMemo(() => {
    if (!isMissingLayerDetail || fetchedLayerMeta?.metadata) return fetchedLayerMeta

    return {
      ...fetchedLayerMeta,
      id: fetchedLayerMeta?.id ?? id ?? '',
      title: fetchedLayerMeta?.title ?? layer?.displayName ?? '',
      suitability: fetchedLayerMeta?.suitability ?? [],
      metadata: {
        layer_id: (id ?? '').split('_')[0],
        title: fetchedLayerMeta?.title ?? layer?.displayName ?? '',
        additional_notes_md: null,
        confidence_rating: null,
        created_at: null,
        description_md: null,
        license: null,
        limitations_caveats_md: null,
        owner_email: null,
        owner_name: null,
        owner_organization: null,
        provider: null,
        source_links: [],
        spatial_extent: null,
      },
    }
  }, [isMissingLayerDetail, fetchedLayerMeta, id, layer])

  if (isLoading) return <DataLayerDetailPageSkeleton />
  if (!id || (isError && !isMissingLayerDetail)) return <DataLayerDetailError />

  return (
    <Box data-testid="data-layer-detail-page" css={page}>
      <Box data-testid="data-layer-detail-page-container" css={container}>
        <Box data-testid="data-layer-detail-content" css={content}>
          <Box data-testid="data-layer-detail-map-pane" css={mapPane}>
            <DataLayerDetailMap
              tilesetIds={activeTilesetIds}
              element={layer?.type === 'Element' ? layer.element : null}
              elementId={id}
              contextual={layer?.type === 'Contextual' ? layer.contextual : null}
              contextualId={id}
            />
          </Box>

          <Box data-testid="data-layer-detail-side-panel" css={sideInner}>
            <Box data-testid="data-layer-detail-header">
              <DataLayerDetailHeader>
                <DataLayerDetailHeaderBackButton />
              </DataLayerDetailHeader>
              <DataLayerDetailHeaderTitle
                displayName={layerMeta?.metadata?.title ?? activeDisplayName}
              />
            </Box>

            <Box data-testid="data-layer-detail-tabs">
              <DataLayerDetailTabs layerType={layer?.type} />
            </Box>
            <Box data-testid="data-layer-detail-outlet">
              <Outlet
                context={{
                  selectedLayerDetail,
                  layer,
                  layerMeta,
                  selectedAssetTag,
                  onSelectScenario: setSelectedAssetTag,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default DataLayerDetailPage
